import { FileUploadQuestion } from "./questionTypes";
import { FileUploadAnswer } from "./answerTypes";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, File, X } from "lucide-react";
import { useState } from "react";

interface FileUploadQuestionProps {
  question: FileUploadQuestion;
  value?: FileUploadAnswer | null;
  onChange: (questionId: string, value: FileUploadAnswer | null) => void;
}

export function FileUploadQuestionComponent({
  question,
  value,
  onChange
}: FileUploadQuestionProps) {
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (file: File | null) => {
    if (file) {
      const answer: FileUploadAnswer = {
        files: [{
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadDate: new Date().toISOString(),
          isValid: true
        }]
      };
      onChange(question.id, answer);
    } else {
      onChange(question.id, null);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      // Check file size
      const maxSize = (question.maxFileSize || 15) * 1024 * 1024; // Convert MB to bytes
      if (file.size > maxSize) {
        alert(`File size must be less than ${question.maxFileSize || 15}MB`);
        return;
      }
      
      handleFileSelect(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file size
      const maxSize = (question.maxFileSize || 15) * 1024 * 1024; // Convert MB to bytes
      if (file.size > maxSize) {
        alert(`File size must be less than ${question.maxFileSize || 15}MB`);
        return;
      }
      
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    handleFileSelect(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{question.title}</h2>
        {question.description && (
          <p className="text-muted-foreground mb-6">{question.description}</p>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          {value && value.files.length > 0 ? (
            // File selected state
            <div className="flex items-center justify-between p-4 border border-border rounded-md bg-muted/50">
              <div className="flex items-center space-x-3">
                <File className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">{value.files[0].fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {(value.files[0].fileSize / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={removeFile}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            // File upload area
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? "border-primary bg-primary/5" 
                  : "border-border hover:border-primary/50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium text-foreground mb-2">
                {question.uploadText || "Upload your shareholder agreement"}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Drag and drop your file here, or click to browse
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                {question.acceptedTypes ? 
                  `Accepted formats: ${question.acceptedTypes.join(", ")}` :
                  "Accepted formats: PDF, DOC, DOCX"
                }
                • Max size: {question.maxFileSize || 15}MB
              </p>
              
              <input
                type="file"
                onChange={handleFileChange}
                accept={question.acceptedTypes?.join(",") || ".pdf,.doc,.docx"}
                className="hidden"
                id={`file-upload-${question.id}`}
              />
              <label htmlFor={`file-upload-${question.id}`}>
                <Button type="button" variant="outline" className="cursor-pointer">
                  Choose File
                </Button>
              </label>
            </div>
          )}
          
          {!question.required && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              This file upload is optional
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}